import { useMemo, useState } from 'react';
import { ProductIdentity } from '@/components/products';
import type { ProductSelectOption } from '@/components/products';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { filterProductsByQuery } from '@/lib/productBarcode';
import { Loader2, Search } from 'lucide-react';

export interface WarehouseAssortmentBulkAddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: ProductSelectOption[];
  loading?: boolean;
  adding?: boolean;
  onAdd: (productIds: string[]) => Promise<void>;
}

export function WarehouseAssortmentBulkAddDialog({
  open,
  onOpenChange,
  products,
  loading = false,
  adding = false,
  onAdd,
}: WarehouseAssortmentBulkAddDialogProps) {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const filteredProducts = useMemo(
    () => filterProductsByQuery(products, search),
    [products, search]
  );

  const allFilteredSelected =
    filteredProducts.length > 0 &&
    filteredProducts.every((product) => selectedIds.includes(product.id));
  const someFilteredSelected =
    filteredProducts.some((product) => selectedIds.includes(product.id)) &&
    !allFilteredSelected;

  function resetSelection() {
    setSearch('');
    setSelectedIds([]);
  }

  function handleOpenChange(next: boolean) {
    if (!next) resetSelection();
    onOpenChange(next);
  }

  function toggleProduct(productId: string, checked: boolean) {
    if (checked) {
      if (selectedIds.includes(productId)) return;
      setSelectedIds((prev) => [...prev, productId]);
      return;
    }
    setSelectedIds((prev) => prev.filter((id) => id !== productId));
  }

  function toggleAllFiltered(checked: boolean) {
    const filteredIds = filteredProducts.map((product) => product.id);
    if (checked) {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...filteredIds])));
      return;
    }
    setSelectedIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
  }

  function selectAllUnassigned() {
    setSelectedIds(products.map((product) => product.id));
  }

  async function handleAdd() {
    if (selectedIds.length === 0) return;
    await onAdd(selectedIds);
    resetSelection();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,720px)] flex-col gap-0 p-0 sm:max-w-2xl">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle>Add products to location</DialogTitle>
          <DialogDescription>
            Select products to assign to this hub or spoke. Already-assigned products are not
            shown.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-3 px-6 py-4">
          <div className="relative shrink-0">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search name, SKU, or barcode…"
              className="pl-9"
              aria-label="Search products"
              disabled={loading || adding}
            />
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span>
              {selectedIds.length} selected
              {search.trim() ? ` · ${filteredProducts.length} shown` : ''}
              {!search.trim() ? ` · ${products.length} available` : ''}
            </span>
            {products.length > 0 && (
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-sm"
                disabled={loading || adding || selectedIds.length === products.length}
                onClick={selectAllUnassigned}
              >
                Select all {products.length}
              </Button>
            )}
          </div>

          {loading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading products…</p>
          ) : products.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              All active products are already assigned to this location.
            </p>
          ) : filteredProducts.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No products match “{search.trim()}”.
            </p>
          ) : (
            <div className="min-h-0 flex-1 overflow-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={
                          allFilteredSelected
                            ? true
                            : someFilteredSelected
                              ? 'indeterminate'
                              : false
                        }
                        onCheckedChange={(checked) => toggleAllFiltered(checked === true)}
                        aria-label="Select all visible products"
                        disabled={adding}
                      />
                    </TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead className="hidden w-32 md:table-cell">SKU</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map((product) => {
                    const checked = selectedIds.includes(product.id);
                    return (
                      <TableRow
                        key={product.id}
                        data-state={checked ? 'selected' : undefined}
                        className="cursor-pointer"
                        onClick={() => toggleProduct(product.id, !checked)}
                      >
                        <TableCell onClick={(event) => event.stopPropagation()}>
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(value) =>
                              toggleProduct(product.id, value === true)
                            }
                            aria-label={`Select ${product.name}`}
                            disabled={adding}
                          />
                        </TableCell>
                        <TableCell>
                          <ProductIdentity
                            productId={product.id}
                            name={product.name}
                            sku={product.sku}
                            size="sm"
                            product={{
                              imageUrl: product.imageUrl ?? product.thumbnailUrl ?? null,
                              media: product.media ?? undefined,
                            }}
                          />
                        </TableCell>
                        <TableCell className="hidden font-mono text-sm md:table-cell">
                          {product.sku}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 border-t px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={adding}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleAdd()}
            disabled={loading || adding || selectedIds.length === 0}
          >
            {adding ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding…
              </>
            ) : (
              `Add ${selectedIds.length > 0 ? selectedIds.length : ''} product${selectedIds.length === 1 ? '' : 's'}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default WarehouseAssortmentBulkAddDialog;
